import { type FC } from "react";
import { Check, ShieldCheck } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import { cn } from "@/shared/utils/cn";
import {
  TIER_CONFIGS,
  BASIC_CARD_FEATURES,
  PREMIUM_CARD_FEATURES,
  PLATINUM_CARD_FEATURES,
  PREMIUM_BADGE,
} from "@/shared/constants/planTiers";
import type { PaymentTier } from "@/shared/utils/tierOrder";

const FeatureCheck: FC<{ text: string; muted?: boolean }> = ({ text, muted }) => (
  <div className="flex items-start gap-2">
    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" strokeWidth={2} />
    <p className={cn("text-sm", muted ? "text-secondary" : "text-primary")}>{text}</p>
  </div>
);

type PaidTier = "basic" | "premium" | "platinum";

const FEATURES_MAP: Record<PaidTier, string[]> = {
  basic: BASIC_CARD_FEATURES,
  premium: PREMIUM_CARD_FEATURES,
  platinum: PLATINUM_CARD_FEATURES,
};

const FREE_BUTTON_LABELS: Record<PaidTier, string> = {
  basic: "Start setup",
  premium: "Find assets",
  platinum: "Start closing",
};

const UPGRADE_BUTTON_LABELS: Record<PaidTier, string> = {
  basic: "Start setup",
  premium: "Find assets",
  platinum: "Settle the estate",
};

function getUpgradePrice(tier: PaidTier, currentTier: PaymentTier | null): string | null {
  if (currentTier === "basic") return TIER_CONFIGS[tier].upgradeFromBasicPrice;
  if (currentTier === "premium") return TIER_CONFIGS[tier].upgradeFromPremiumPrice ?? null;
  return null;
}

interface TierCardProps {
  tier: PaidTier;
  currentTier?: PaymentTier | null;
  onCheckout: (tier: PaymentTier) => void;
  isLoading: boolean;
  loadingTier: PaymentTier | null;
  subtitleSize?: "xs" | "sm";
}

export const TierCard: FC<TierCardProps> = ({
  tier,
  currentTier = null,
  onCheckout,
  isLoading,
  loadingTier,
  subtitleSize = "xs",
}) => {
  const isCurrent = tier === currentTier;
  const isFree = !currentTier || currentTier === "free";
  const isUpgrade = !isCurrent && !isFree;
  const config = TIER_CONFIGS[tier];
  const features = FEATURES_MAP[tier];
  const prefix = config.includesPrefix ?? null;
  const upgradePrice = isUpgrade ? getUpgradePrice(tier, currentTier) : null;
  const showBadge = tier === "premium" && !isCurrent;
  const showMostPopular = tier === "premium" && isFree;
  const useBrandBorder = (isFree && tier === "premium") || isUpgrade;

  const headerBg = isCurrent ? "bg-white" : useBrandBorder ? "bg-brand-subtle" : "bg-subtle";

  const buttonText = isCurrent
    ? "Your current plan"
    : isFree
      ? FREE_BUTTON_LABELS[tier]
      : UPGRADE_BUTTON_LABELS[tier];

  return (
    <div className="relative flex min-w-0 flex-1 flex-col">
      {showMostPopular && (
        <span className="absolute left-4 top-0 z-10 -translate-y-1/2 rounded-full bg-brand px-2.5 py-0.5 text-xs font-medium text-white">
          Most popular
        </span>
      )}
      <div
        className={cn(
          "flex flex-1 flex-col overflow-hidden rounded-md bg-white",
          useBrandBorder ? "border-[1.5px] border-brand" : "border border-primary",
        )}
      >
        <div
          className={cn(
            "flex flex-col items-start gap-1 p-4",
            !useBrandBorder && "border-b border-primary",
            headerBg,
          )}
        >
          <p className="text-xl font-semibold">{config.name}</p>
          {isCurrent ? (
            <div className="flex items-start">
              <Tag color="white" size="sm">
                Current plan
              </Tag>
            </div>
          ) : (
            <p className={cn(subtitleSize === "sm" ? "text-sm" : "text-xs", "text-secondary")}>
              {config.subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <div className={cn("flex flex-1 flex-col gap-2", currentTier === "premium" && "pb-4")}>
            {prefix && <p className="text-sm font-medium">{prefix}</p>}
            {features.map((feature) => (
              <FeatureCheck key={feature} text={feature} muted={isCurrent} />
            ))}
          </div>
          {showBadge && (
            <Tag
              color="green"
              size="sm"
              icon={ShieldCheck}
              className="self-start bg-green-100 text-brand"
            >
              {PREMIUM_BADGE}
            </Tag>
          )}
          {isCurrent ? (
            <Button
              variant="outline"
              rounded
              className={cn("w-full border border-primary bg-white")}
              disabled
            >
              {buttonText}
            </Button>
          ) : (
            <>
              {upgradePrice ? (
                <div className="flex items-baseline gap-1">
                  <p className="text-xl text-tertiary line-through">{config.price}</p>
                  <p className="text-xl font-semibold text-brand">{upgradePrice}</p>
                  <p className="text-xs text-secondary">To upgrade</p>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <p className="text-2xl font-semibold">{config.price}</p>
                  {tier === "basic" && (
                    <p className="text-xs text-secondary">
                      <span className="font-bold">Save $4,500</span> in attorney time on average
                    </p>
                  )}
                  {tier === "platinum" && (
                    <p className="text-xs text-secondary">
                      Comparable concierge plans average <span className="font-bold">$14,700</span>
                    </p>
                  )}
                </div>
              )}
              <Button
                variant={useBrandBorder ? "brand" : "outline"}
                rounded
                className="w-full"
                onClick={() => onCheckout(tier)}
                disabled={isLoading}
              >
                {loadingTier === tier ? "Loading..." : buttonText}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface TierCardGridProps {
  currentTier?: PaymentTier | null;
  onCheckout: (tier: PaymentTier) => void;
  isLoading: boolean;
  loadingTier: PaymentTier | null;
  subtitleSize?: "xs" | "sm";
}

export const TierCardGrid: FC<TierCardGridProps> = ({
  currentTier = null,
  onCheckout,
  isLoading,
  loadingTier,
  subtitleSize,
}) => {
  const tiers: PaidTier[] =
    currentTier === "premium" ? ["premium", "platinum"] : ["basic", "premium", "platinum"];

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4",
        currentTier === "premium" ? "md:grid md:grid-cols-2" : "md:grid md:grid-cols-3",
      )}
    >
      {tiers.map((tier) => (
        <TierCard
          key={tier}
          tier={tier}
          currentTier={currentTier}
          onCheckout={onCheckout}
          isLoading={isLoading}
          loadingTier={loadingTier}
          subtitleSize={subtitleSize}
        />
      ))}
    </div>
  );
};
